// store/scheduleColorsSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"

const persistConfig = {
  key: "scheduleColors",
  storage,
}

const initialState = {
  scheduleColors: {},
  status: "idle",
  error: null,
}

const getColorForScheduleUid = (scheduleUid) => {
  // Create a hash from the schedule_uid
  let hash = 0
  for (let i = 0; i < scheduleUid.length; i++) {
    hash = (hash << 5) - hash + scheduleUid.charCodeAt(i)
    hash = hash & hash // Convert to 32-bit integer
  }

  // Make sure hash is positive
  hash = Math.abs(hash)

  // Use the golden ratio to help spread the hues around the color wheel
  const goldenRatio = 0.618033988749895
  const hueRotation = (((hash * goldenRatio) % 1) + 1) % 1 // Ensure positive value

  // Convert to degrees and ensure good saturation and lightness for visibility
  const hue = Math.floor(hueRotation * 360)
  const saturation = 65 + (hash % 20) // Range: 65-85%
  const lightness = 45 + (hash % 15) // Range: 45-60%

  return `hsl(${hue}, ${saturation}%, ${lightness}%)`
}

export const initializeScheduleColors = createAsyncThunk(
  "scheduleColors/initializeScheduleColors",
  async (schedules, thunkAPI) => {
    try {
      const colors = {}
      schedules.forEach((schedule) => {
        if (schedule.schedule_uid && !colors[schedule.schedule_uid]) {
          colors[schedule.schedule_uid] = getColorForScheduleUid(schedule.schedule_uid)
        }
      })
      return colors
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message)
    }
  }
)

const scheduleColorsSlice = createSlice({
  name: "scheduleColors",
  initialState,
  reducers: {
    setScheduleColors: (state, action) => {
      state.scheduleColors = action.payload
    },
    addScheduleColor: (state, action) => {
      const { schedule_uid } = action.payload
      if (schedule_uid && !state.scheduleColors[schedule_uid]) {
        state.scheduleColors[schedule_uid] = getColorForScheduleUid(schedule_uid)
      }
    },
    removeScheduleColor: (state, action) => {
      const { schedule_uid } = action.payload
      delete state.scheduleColors[schedule_uid]
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initializeScheduleColors.pending, (state) => {
        state.status = "loading"
      })
      .addCase(initializeScheduleColors.fulfilled, (state, action) => {
        state.status = "succeeded"
        state.scheduleColors = { ...state.scheduleColors, ...action.payload }
      })
      .addCase(initializeScheduleColors.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload
      })
  },
})

export const { setScheduleColors, addScheduleColor, removeScheduleColor } = scheduleColorsSlice.actions

const persistedReducer = persistReducer(persistConfig, scheduleColorsSlice.reducer)
export default persistedReducer
